import { useQuery, useQueryClient } from '@tanstack/react-query'
import isNil from 'lodash/isNil'
import { useRef } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { sleep } from '@omnea-app/fe-timing/sleep'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { UserFilter } from '@omnea-app/users/usersFrontendClient'
import { UserResponse, UsersHttpClient } from '@omnea-app/users/usersHttpClient'
import { useSetUsersByIdCache } from './useGetUsersByIds'

type User = NonNullable<UserResponse<'/search', 'get'>['data']>[number]

interface Props<MappedValue = User[]> {
  search: string
  limit?: number
  offset?: number
  filter?: UserFilter
  debounceType?: DebounceType
  /** This function requires a stable reference, otherwise it will run on every render */
  select?: (data?: User[]) => MappedValue
  enabled?: boolean
  minSearchLength?: number
}

type QueryKey = [
  'searchUsers',
  {
    isEnabled: boolean
    search: string
    limit: number | undefined
    offset: number | undefined
    filter: UserFilter | undefined
  },
]

export const useSearchUsers = <MappedValue = User[]>({
  search: rawSearch,
  limit,
  offset,
  filter,
  debounceType = 'textInput',
  select = undefined,
  minSearchLength = 1,
  enabled: enabledProp = true,
}: Props<MappedValue>) => {
  const queryClient = useQueryClient()
  const { getToken } = useAuthenticatedUser()

  const search = rawSearch.trim().toLowerCase()

  const filterWithDefaults = filter ? { excludeArchived: true, ...filter } : { excludeArchived: true }

  const isEnabled = enabledProp && search.length >= minSearchLength

  const queryKey: QueryKey = [
    'searchUsers',
    {
      isEnabled,
      search,
      limit,
      offset,
      filter: filterWithDefaults,
    },
  ]

  // Used for debouncing
  // Deboucing inside of `useQuery` works best because it allows to return
  // cached data instantly, and if we have partial previous results due to the user
  // typing, that is returned instantly too
  const activeQueryRef = useRef<QueryKey | null>(null)

  const setUsersByIdCache = useSetUsersByIdCache()

  return useQuery({
    queryKey,
    queryFn: async ({ queryKey }) => {
      activeQueryRef.current = queryKey

      const debounceTimeout = timeoutByDebounceType[debounceType]
      await sleep(debounceTimeout)

      const queryWasAborted = activeQueryRef.current !== queryKey
      if (queryWasAborted) {
        await queryClient.cancelQueries({ queryKey })
        return
      }

      const token = await getToken()
      const apiClient = new UsersHttpClient({ omneaJwt: token })
      const { data, error } = await apiClient.GET('/search', {
        params: {
          query: {
            search,
            limit,
            offset,
            filter: filterWithDefaults,
          },
        },
      })
      throwIfApiError(error)

      setUsersByIdCache(data)

      return data
    },
    select,
    placeholderData: (previousData, previousQuery) => {
      if (!isEnabled) return undefined
      return keepPreviousResultsIfTyping(search, previousData, previousQuery?.queryKey[1].search)
    },
    enabled: isEnabled,
  })
}

const keepPreviousResultsIfTyping = (
  currentSearch: string,
  previousResults: User[] | undefined,
  previousSearch: string | undefined
) => {
  if (isNil(previousSearch)) return undefined

  // This could be improved by searching the cache for previous searches that are a substring of the current search
  const userIsTyping = currentSearch.startsWith(previousSearch)

  // If the current query is a continuation of the previous query, we keep the previous data
  if (userIsTyping) return previousResults

  return undefined
}

const timeoutByDebounceType = {
  textInput: 300,
  richTextMention: 100,
}

type DebounceType = keyof typeof timeoutByDebounceType
